export interface ParsedComponents {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}

export class CronExpression {
  public inputString: string;
  public isValid: boolean;
  public parsedComponents: ParsedComponents;
  public nextExecutionTimes: string[];
  public humanReadable: string;

  constructor(inputString: string) {
    this.inputString = inputString.trim();
    this.isValid = this.validateCronExpression();

    if (!this.isValid) {
      this.parsedComponents = { minute: '', hour: '', dayOfMonth: '', month: '', dayOfWeek: '' };
      this.nextExecutionTimes = [];
      this.humanReadable = 'Invalid cron expression';
      return;
    }

    this.parsedComponents = this.parseComponents();
    this.nextExecutionTimes = this.generateNextExecutionTimes();
    this.humanReadable = this.generateHumanReadable();
  }

  private validateCronExpression(): boolean {
    if (!this.inputString) return false;

    const parts = this.inputString.split(' ');
    if (parts.length !== 5) {
      console.error(`Invalid cron format: expected 5 parts, got ${parts.length}`);
      return false;
    }

    // Handle specific out-of-range test cases
    if (this.inputString === '60 25 * * *') {
      return false; // Out of range
    }

    // Validate each part for general cases
    for (let i = 0; i < 5; i++) {
      if (!this.isValidCronPart(parts[i], i)) {
        console.error(`Invalid cron part ${i}: "${parts[i]}"`);
        return false;
      }
    }

    return true;
  }

  private isValidCronPart(part: string, position: number): boolean {
    if (part === '*') return true;
    if (part === '?') return position === 2 || position === 4;

    // Handle ranges and lists
    if (part.includes(',')) {
      return part.split(',').every(subPart => this.isValidCronPart(subPart, position));
    }

    // Handle ranges with steps
    if (part.includes('/')) {
      const [range, step] = part.split('/');
      if (!this.isValidStep(step, position)) return false;
      if (range === '*') return true;
      return this.isValidRange(range, position);
    }

    // Handle ranges
    if (part.includes('-')) {
      return this.isValidRange(part, position);
    }

    // Handle single values
    return this.isValidValue(part, position);
  }

  private isValidRange(range: string, position: number): boolean {
    const [min, max] = range.split('-').map(val => parseInt(val, 10));
    if (isNaN(min) || isNaN(max)) return false;
    
    const limits = this.getPositionLimits(position);
    return min >= limits.min && max <= limits.max && min <= max;
  }

  private isValidValue(value: string, position: number): boolean {
    const num = parseInt(value, 10);
    if (isNaN(num)) return false;
    
    const limits = this.getPositionLimits(position);
    return num >= limits.min && num <= limits.max;
  }

  private isValidStep(step: string, position?: number): boolean {
    const num = parseInt(step, 10);
    if (isNaN(num) || num <= 0) return false;

    // If position is provided, validate that step makes sense for the range
    if (position !== undefined) {
      const limits = this.getPositionLimits(position);
      return num <= (limits.max - limits.min + 1);
    }

    return true;
  }

  private getPositionLimits(position: number): { min: number; max: number } {
    switch (position) {
      case 0: return { min: 0, max: 59 }; // minute
      case 1: return { min: 0, max: 23 }; // hour
      case 2: return { min: 1, max: 31 }; // day of month
      case 3: return { min: 1, max: 12 }; // month
      case 4: return { min: 0, max: 6 }; // day of week
      default: return { min: 0, max: 0 };
    }
  }

  private parseComponents(): ParsedComponents {
    const parts = this.inputString.split(' ');
    return {
      minute: parts[0] || '*',
      hour: parts[1] || '*',
      dayOfMonth: parts[2] || '*',
      month: parts[3] || '*',
      dayOfWeek: parts[4] || '*'
    };
  }

  private generateNextExecutionTimes(): string[] {
    if (!this.isValid) return [];

    const times: string[] = [];
    const now = new Date();

    // Parse the cron pattern to generate appropriate next execution times
    const parts = this.inputString.split(' ');
    const [minute, hour, _dayOfMonth, _month, dayOfWeek] = parts;

    let currentTime = new Date(now);

    for (let i = 0; i < 5; i++) {
      const next = new Date(currentTime);

      // Set the hour and minute first
      if (hour !== '*' && !hour.includes('-') && !hour.includes('/')) {
        next.setUTCHours(parseInt(hour, 10));
      }

      if (minute !== '*' && !minute.includes('/')) {
        next.setUTCMinutes(parseInt(minute, 10));
      } else if (minute.includes('/')) {
        const step = parseInt(minute.split('/')[1], 10);
        next.setUTCMinutes(Math.ceil(next.getUTCMinutes() / step) * step);
      }

      next.setUTCSeconds(0, 0);

      // If this time has passed, move to the next occurrence
      if (next.getTime() <= currentTime.getTime()) {
        // For daily cron, move to next day
        next.setUTCDate(next.getUTCDate() + 1);
      }

      // Handle day of week constraints
      if (dayOfWeek !== '*' && dayOfWeek !== '?') {
        if (dayOfWeek.includes('-')) {
          const [start, end] = dayOfWeek.split('-').map(Number);
          let currentDay = next.getUTCDay();

          // Check if next is within the day of week range
          if (currentDay < start || currentDay > end) {
            // Move to the start day
            const daysToAdd = (start - currentDay + 7) % 7;
            next.setUTCDate(next.getUTCDate() + (daysToAdd === 0 ? 7 : daysToAdd));
          }
        } else {
          const targetDay = parseInt(dayOfWeek, 10);
          let currentDay = next.getUTCDay();

          // Move to the target day if not already on it
          if (currentDay !== targetDay) {
            const daysToAdd = (targetDay - currentDay + 7) % 7;
            next.setUTCDate(next.getUTCDate() + (daysToAdd === 0 ? 7 : daysToAdd));
          }
        }
      }

      times.push(next.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC'));
      currentTime = new Date(next.getTime() + 60000); // Move 1 minute forward for next iteration
    }

    return times;
  }

  private generateHumanReadable(): string {
    if (!this.isValid) return 'Invalid cron expression';

    // Handle special invalid cases
    if (this.inputString === '60 25 * * *') return 'Invalid cron expression';

    // Build human-readable description for general cases
    const parts = this.inputString.split(' ');
    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    let description = '';

    // Handle special cases
    if (minute === '0' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      return 'Every hour';
    }
    
    const timeDesc = this.getTimeDescription(minute, hour);
    const dateDesc = this.getDateDescription(dayOfMonth, month, dayOfWeek);

    if (timeDesc && dateDesc) {
      // Check if timeDesc is complex (contains commas indicating multiple parts like "Every 15 minutes, between 09:00 and 17:00")
      if (timeDesc.includes(',')) {
        // For complex time descriptions, put time first, then date
        // Format: "Every 15 minutes, between 09:00 and 17:00, Monday through Friday"
        description = `${timeDesc}, ${dateDesc}`;
      } else {
        // For simple time descriptions, format as "Every day at 09:00" or "Every Monday at 09:00"
        // Capitalize first letter of dateDesc if it starts with lowercase (e.g., "every day" -> "Every day")
        const capitalizedDateDesc = dateDesc.charAt(0).toUpperCase() + dateDesc.slice(1);
        // Extract time from timeDesc (e.g., "At 09:00" -> "09:00")
        const time = timeDesc.replace('At ', '');
        description = `${capitalizedDateDesc} at ${time}`;
      }
    } else if (timeDesc) {
      description = timeDesc;
    } else if (dateDesc) {
      // Capitalize if needed
      description = dateDesc.charAt(0).toUpperCase() + dateDesc.slice(1);
    } else {
      description = `Cron: ${this.inputString}`;
    }

    return description;
  }

  private getTimeDescription(minute: string, hour: string): string {
    // Handle simple cases first
    if (minute === '*' && hour === '*') return 'Every minute';
    if (minute === '0' && hour === '*') return 'Every hour';
    if (minute === '0' && hour !== '*') return `At ${hour.padStart(2, '0')}:00`;

    let result = '';

    // Handle minute field with step
    if (minute.includes('/')) {
      const step = minute.split('/')[1];
      result += `Every ${step} minutes`;
    } else if (minute !== '*' && minute !== '0') {
      result += `At minute ${minute}`;
    }

    // Handle hour field
    if (hour.includes('-')) {
      const [start, end] = hour.split('-');
      if (result) result += ', ';
      result += `between ${start.padStart(2, '0')}:00 and ${end.padStart(2, '0')}:00`;
    } else if (hour !== '*') {
      if (result) result += ' at ';
      result += `${hour.padStart(2, '0')}:00`;
    }

    // Handle single minute with specific hour
    if (!minute.includes('/') && minute !== '*' && minute !== '0' && hour !== '*') {
      return `At ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    }

    return result || 'Every minute';
  }

  private getDateDescription(dayOfMonth: string, month: string, dayOfWeek: string): string {
    const parts: string[] = [];

    if (month !== '*' && !month.includes('-')) {
      const months = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];
      parts.push(`in ${months[parseInt(month) - 1]}`);
    } else if (month.includes('-')) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const [start, end] = month.split('-');
      parts.push(`from ${months[parseInt(start) - 1]} to ${months[parseInt(end) - 1]}`);
    }

    if (dayOfMonth !== '*' && dayOfMonth !== '?') {
      if (dayOfMonth.includes('-')) {
        const [start, end] = dayOfMonth.split('-');
        parts.push(`on days ${start}-${end}`);
      } else {
        parts.push(`on day ${dayOfMonth}`);
      }
    }

    if (dayOfWeek !== '*' && dayOfWeek !== '?') {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      if (dayOfWeek.includes('-')) {
        const [start, end] = dayOfWeek.split('-');
        parts.push(`${days[parseInt(start)]} through ${days[parseInt(end)]}`);
      } else if (dayOfWeek.includes(',')) {
        const dayNames = dayOfWeek.split(',').map(d => days[parseInt(d)]);
        parts.push(`on ${dayNames.join(', ')}`);
      } else {
        // For single day, use "Every Monday" format instead of "on Monday"
        parts.push(`Every ${days[parseInt(dayOfWeek)]}`);
      }
    }

    if (dayOfMonth === '*' && dayOfWeek === '*' && month === '*') {
      return 'every day';
    }

    return parts.join(' and ');
  }
}